import React, { useEffect, useState } from 'react';
import { Globe, TrendingUp, Music, Users, RefreshCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * LastFmPulseGeo
 * --------------------------------------------------------------------------
 * Renders live geo/global charts inside The Pulse → MUSIC DEEP DIVE slide-out.
 *   • selectedRegion.level === 'global'   → chart.getTopTracks + chart.getTopArtists
 *   • selectedRegion.level === 'country'  → geo.getTopTracks + geo.getTopArtists for that country
 *   • selectedRegion.level === 'state'    → falls back to the parent country from path
 *   • selectedRegion.level === 'continent'→ skipped (Last.fm has no continent endpoint)
 * Applies the user's genre filter client-side via tag matching when available.
 * Hides itself entirely when there's no data + no error (no empty slot).
 */
const LastFmPulseGeo = ({ selectedRegion, filterGenre }) => {
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [scope, setScope] = useState(null); // {label, type, country?}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resolveScope = (region) => {
    if (!region) return null;
    if (region.level === 'global') return { type: 'global', label: 'Worldwide' };
    if (region.level === 'country') return { type: 'country', label: region.name, country: region.name };
    if (region.level === 'state') {
      // Fall back to parent country from breadcrumb path: ['Global', 'United States', 'California']
      const parent = Array.isArray(region.path) && region.path.length >= 2 ? region.path[region.path.length - 2] : null;
      if (parent && parent !== 'Global') return { type: 'country', label: parent, country: parent };
    }
    return null;
  };

  const fetchData = async (signal) => {
    const sc = resolveScope(selectedRegion);
    setScope(sc);
    if (!sc) {
      setTracks([]); setArtists([]); return;
    }
    setLoading(true); setError(null);
    try {
      const limit = 12;
      const [tUrl, aUrl] = sc.type === 'global'
        ? [`${BACKEND_URL}/api/lastfm/chart/top-tracks?limit=${limit}`, `${BACKEND_URL}/api/lastfm/chart/top-artists?limit=${limit}`]
        : [`${BACKEND_URL}/api/lastfm/geo/top-tracks?country=${encodeURIComponent(sc.country)}&limit=${limit}`, `${BACKEND_URL}/api/lastfm/geo/top-artists?country=${encodeURIComponent(sc.country)}&limit=${limit}`];

      const [tr, ar] = await Promise.all([
        fetch(tUrl, { signal }).then(r => r.ok ? r.json() : Promise.reject(new Error(`tracks ${r.status}`))),
        fetch(aUrl, { signal }).then(r => r.ok ? r.json() : Promise.reject(new Error(`artists ${r.status}`))),
      ]);
      setTracks(Array.isArray(tr?.track) ? tr.track : []);
      setArtists(Array.isArray(ar?.artist) ? ar.artist : []);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Failed to load Last.fm data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    fetchData(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line
  }, [selectedRegion?.id, selectedRegion?.level, selectedRegion?.name]);

  // Skip continents entirely
  if (selectedRegion?.level === 'continent') return null;
  if (!scope) return null;
  if (!loading && !error && tracks.length === 0 && artists.length === 0) return null;

  // Apply genre filter client-side when user picked one (matches against ListenBrainz-style tag — Last.fm chart endpoints don't return per-track tags, so this is best-effort)
  const matchGenre = (item) => {
    if (!filterGenre || filterGenre === 'All Genres') return true;
    const needle = String(filterGenre).toLowerCase();
    const blob = `${item?.name || ''} ${item?.artist?.name || item?.artist || ''}`.toLowerCase();
    return blob.includes(needle);
  };
  const filteredTracks = tracks.filter(matchGenre);
  const filteredArtists = artists.filter(matchGenre);

  return (
    <div data-testid="lastfm-pulse-geo" style={{
      padding: '14px 16px',
      borderRadius: '12px',
      background: 'linear-gradient(135deg, rgba(6,182,212,0.06), rgba(192,38,211,0.06))',
      border: '1px solid rgba(0, 255, 255, 0.18)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={16} style={{ color: '#06b6d4' }} />
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#00FFFF', letterSpacing: '0.5px' }}>
            LIVE FROM {scope.label.toUpperCase()}
          </span>
          <span style={{ fontSize: '10px', color: '#888' }}>· Last.fm</span>
        </div>
        <button
          data-testid="lastfm-pulse-refresh"
          onClick={() => fetchData()}
          disabled={loading}
          style={{ background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer', color: '#888', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}
          title="Refresh"
        >
          <RefreshCcw size={11} className={loading ? 'animate-spin' : ''} />
          {loading ? '...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: '11px', color: '#ff6b6b', marginBottom: '10px' }}>{error}</div>
      )}

      {/* TOP TRACKS */}
      {filteredTracks.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#06b6d4', marginBottom: '8px', letterSpacing: '0.4px' }}>
            <TrendingUp size={12} /> TOP TRACKS <span style={{ color: '#64748b', fontSize: '10px', fontWeight: 500 }}>({filteredTracks.length})</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {filteredTracks.slice(0, 10).map((t, idx) => {
              const artistName = (typeof t.artist === 'object' ? t.artist?.name : t.artist) || '';
              return (
                <a
                  key={`t-${idx}`}
                  href={t.url || `https://www.last.fm/music/${encodeURIComponent(artistName)}/_/${encodeURIComponent(t.name || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`lastfm-pulse-track-${idx}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 10px', borderRadius: '8px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    color: '#fff', textDecoration: 'none', transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(6,182,212,0.08)'; e.currentTarget.style.borderColor = 'rgba(6,182,212,0.25)'; e.currentTarget.style.transform = 'translateX(3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <span style={{ flexShrink: 0, width: '22px', fontSize: '11px', fontWeight: 800, color: '#06b6d4', textAlign: 'center' }}>#{idx + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{artistName}</div>
                  </div>
                  {t.listeners && (
                    <div style={{ fontSize: '9px', color: '#64748b', flexShrink: 0 }}>
                      {Number(t.listeners).toLocaleString()} listeners
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* TOP ARTISTS */}
      {filteredArtists.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: '#c026d3', marginBottom: '8px', letterSpacing: '0.4px' }}>
            <Users size={12} /> TOP ARTISTS <span style={{ color: '#64748b', fontSize: '10px', fontWeight: 500 }}>({filteredArtists.length})</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {filteredArtists.slice(0, 12).map((a, idx) => (
              <a
                key={`a-${idx}`}
                href={a.url || `https://www.last.fm/music/${encodeURIComponent(a.name || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`lastfm-pulse-artist-${idx}`}
                style={{
                  padding: '6px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                  background: 'rgba(192,38,211,0.08)',
                  border: '1px solid rgba(192,38,211,0.25)',
                  color: '#e879f9', textDecoration: 'none', transition: 'all 0.2s',
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(192,38,211,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(192,38,211,0.08)'; e.currentTarget.style.transform = 'none'; }}
              >
                <Music size={10} />
                {a.name}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && tracks.length === 0 && artists.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`sk-${i}`} style={{
              height: '36px', borderRadius: '8px',
              background: 'linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.08), rgba(255,255,255,0.04))',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.4s linear infinite',
            }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LastFmPulseGeo;
